import { Position } from '@xyflow/react'
import { layoutFromMap } from 'entitree-flex'

import type { Edge, Node } from '@xyflow/react'

/** Напрямок росту дерева: зверху вниз / зліва направо. */
export type TreeDirection = 'TB' | 'LR'

/** Мінімум, який шар розкладки знає про елемент. */
export interface TreeItemBase {
    id: string
    parentId: string | null
}

interface LayoutOptions {
    direction: TreeDirection
    nodeWidth: number
    nodeHeight: number
    /** Відступ між сусідніми деревами, коли коренів кілька. */
    forestSpacing: number
}

/** Форма, у якій entitree-flex приймає дерево: мапа id → вузол зі списком дітей. */
type EntitreeMap = Record<string, { id: string; children: string[] }>

interface EntitreeNode {
    id: string
    x: number
    y: number
    isSpouse?: boolean
    isSibling?: boolean
}

interface EntitreeRel {
    source: { id: string }
    target: { id: string; isSpouse?: boolean; isSibling?: boolean }
}

const { Top, Bottom, Left, Right } = Position

/**
 * Налаштування entitree-flex. `spouses`/`siblings` бібліотека вимагає, але
 * ієрархія «батько → діти» їх не використовує: акцесори лишаються, аби
 * бібліотека не шукала неіснуючі поля, а спорідненість завжди порожня.
 */
const ENTITREE_SETTINGS = {
    clone: true,
    enableFlex: true,
    firstDegreeSpacing: 40,
    nextAfterAccessor: 'spouses',
    nextAfterSpacing: 40,
    nextBeforeAccessor: 'siblings',
    nextBeforeSpacing: 40,
    rootX: 0,
    rootY: 0,
    secondDegreeSpacing: 40,
    sourcesAccessor: 'parents',
    sourceTargetSpacing: 40,
    targetsAccessor: 'children',
}

/**
 * Індекс «id → діти» плюс перелік коренів.
 *
 * Коренем вважається не лише елемент без `parentId`, а й той, чий батько не
 * потрапив у вибірку: інакше піддерево тихо зникло б з екрана (типово для
 * фільтрованих або посторінкових даних).
 */
export const buildForest = <T extends TreeItemBase>(items: T[]): { map: EntitreeMap; rootIds: string[] } => {
    const known = new Set(items.map((item) => item.id))
    const map: EntitreeMap = {}
    const rootIds: string[] = []

    for (const item of items) {
        map[item.id] = { ...item, id: item.id, children: [] }
    }

    for (const item of items) {
        const parentId = item.parentId
        const parent = parentId !== null && known.has(parentId) ? map[parentId] : undefined

        if (parent) {
            parent.children.push(item.id)
        } else {
            rootIds.push(item.id)
        }
    }

    return { map, rootIds }
}

const positionsFor = (direction: TreeDirection, node: { isSpouse?: boolean; isSibling?: boolean }) => {
    const isHorizontal = direction === 'LR'

    if (node.isSpouse) {
        return { source: isHorizontal ? Bottom : Right, target: isHorizontal ? Top : Left }
    }

    if (node.isSibling) {
        return { source: isHorizontal ? Top : Left, target: isHorizontal ? Bottom : Right }
    }

    return { source: isHorizontal ? Right : Bottom, target: isHorizontal ? Left : Top }
}

/**
 * Розкладка лісу в координати xyflow.
 *
 * entitree-flex рахує рівно одне дерево за виклик, тому кожен корінь
 * розкладається окремо, а результати зсуваються вздовж «поперечної» осі:
 * вертикальне дерево росте вниз — сусідні дерева стають праворуч, і навпаки.
 *
 * Зсув рахується від МЕЖ дерева, а не від максимуму координат: entitree
 * центрує гілку навколо кореня і вільно видає від'ємні x, тож дерево з
 * дітьми починається лівіше за власний корінь. Якщо це не врахувати, сусідні
 * гілки наїжджають одна на одну — саме так поводився каталог із трьома
 * коренями.
 */
export const layoutForest = <T extends TreeItemBase>(
    items: T[],
    { direction, nodeWidth, nodeHeight, forestSpacing }: LayoutOptions
): { nodes: Node[]; edges: Edge[] } => {
    const { map, rootIds } = buildForest(items)
    const isHorizontal = direction === 'LR'

    const nodes: Node[] = []
    const edges: Edge[] = []
    let offset = 0

    for (const rootId of rootIds) {
        const { nodes: treeNodes, rels: treeEdges } = layoutFromMap(rootId, map, {
            ...ENTITREE_SETTINGS,
            nodeWidth,
            nodeHeight,
            orientation: isHorizontal ? 'horizontal' : 'vertical',
        }) as { nodes: EntitreeNode[]; rels: EntitreeRel[] }

        // Межі гілки вздовж поперечної осі (для вертикального дерева — по x)
        const crossOf = (node: EntitreeNode): number => (isHorizontal ? node.y : node.x)
        const crossSize = isHorizontal ? nodeHeight : nodeWidth
        const min = Math.min(...treeNodes.map(crossOf))
        const max = Math.max(...treeNodes.map((node) => crossOf(node) + crossSize))

        // Ставимо ліву (верхню) межу гілки рівно на поточний offset
        const shift = offset - min

        for (const node of treeNodes) {
            const { source, target } = positionsFor(direction, node)
            const x = isHorizontal ? node.x : node.x + shift
            const y = isHorizontal ? node.y + shift : node.y

            nodes.push({
                id: node.id,
                type: 'domain',
                position: { x, y },
                width: nodeWidth,
                height: nodeHeight,
                sourcePosition: source,
                targetPosition: target,
                data: { itemId: node.id, direction, isRoot: node.id === rootId },
            })
        }

        for (const rel of treeEdges) {
            const { source, target } = positionsFor(direction, rel.target)

            edges.push({
                id: `e-${rel.source.id}-${rel.target.id}`,
                source: rel.source.id,
                target: rel.target.id,
                type: 'smoothstep',
                animated: false,
                sourceHandle: source,
                targetHandle: target,
            })
        }

        offset += max - min + forestSpacing
    }

    return { nodes, edges }
}
